import { Box, Heading } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type SourcingEventDto,
  type TextDocumentDto,
  useCreateSourcingEventTextDocumentMutation,
  useListTextDocumentsForSourcingEventQuery,
  type ViewDocumentAsOwnerDto,
} from "../../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload, UploadCompletedStatus } from "../../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../../common/documents/UppyUploader/useUppyUploader";
import { StepNavigation } from "../../../../../common/StepNavigation";

interface Props {
  eventId: string;
  event: SourcingEventDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  refetch: () => void;
  isActive: boolean;
}

export const keepLocalEditsWhileSameDocumentSet = <T extends { id: string }>(current: T[], server: T[]): T[] => {
  const sameDocumentSet =
    current.length === server.length && server.every((serverDoc) => current.some((doc) => doc.id === serverDoc.id));
  return sameDocumentSet ? current : server;
};

export const DocumentsStep = ({ eventId, event, previousStep, nextStep, nextStepLabel, refetch, isActive }: Props) => {
  const { t } = useTranslation();

  const { uppy, allFiles, documents, setDocuments, removeUpload, reloadEntity } = useUppyUploader({
    entityType: "SourcingEventV2",
    entityId: eventId,
    reloadEntity: refetch,
  });

  const [createTextDocument, { isLoading: isCreatingTextDocument }] = useCreateSourcingEventTextDocumentMutation();
  const { data: textDocumentRes, isLoading: isLoadingTextDocuments } = useListTextDocumentsForSourcingEventQuery({
    eventId,
  });
  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);

  useEffect(() => {
    if (isActive) refetch();
  }, [isActive, refetch]);

  useEffect(() => {
    const serverDocuments = event.eventData.documents ?? [];
    setDocuments((current) => keepLocalEditsWhileSameDocumentSet(current, serverDocuments));
  }, [event.eventData.documents, setDocuments]);

  useEffect(() => {
    if (!textDocumentRes) return;
    setTextDocuments((current) => keepLocalEditsWhileSameDocumentSet(current, textDocumentRes.documents));
  }, [textDocumentRes]);

  const createTextDocumentForEvent = useCallback(
    () =>
      createTextDocument({
        createSourcingEventTextDocumentRequest: { eventId, name: "", text: "", tags: [] },
      }).unwrap(),
    [createTextDocument, eventId]
  );

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="6">
        {t("Documents")}
      </Heading>

      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        textDocuments={textDocuments}
        setTextDocuments={setTextDocuments}
        createTextDocument={createTextDocumentForEvent}
        isCreatingTextDocument={isCreatingTextDocument}
        canEditTextDocuments
        isLoading={isLoadingTextDocuments}
        removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
          removeUpload({
            status: UploadCompletedStatus.UploadCompleted,
            documentId: document.id,
            uploadId: document.fromUploadId,
            ...document,
          })
        }
        isTagsColumnVisible
        isCategoryColumnVisible
        canEditCategory
        canDeleteDocuments
        canEditDocumentNames
        documentUploadConfig={{
          uppy,
          dropTargetId: `documents-step-${eventId}`,
          dropTargetHelperText: t("Drag and drop files here, or click to browse"),
          uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
          removeUpload,
        }}
        refetchDocuments={() => { reloadEntity(); }}
      />

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
